// Dependencies
import React, { useMemo } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  AnalyticsPageView,
  AnalyticsProvider,
} from '@nerdwallet/seo-analytics';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';
// Local Components
import Results from '../../components/currency/results/results';
import {
  FTHBProgram,
  RatesTrend,
  RoundupLink,
  PageTitle,
} from '../../components/mortgage-calculator/mortgage-state';
import {
  MORTGAGE_STATE_CALCULATOR,
  MCControlPanel,
  MCResultsPanel,
  MORTGAGE_STATE,
} from '../../components/mortgage-calculator/shared';
import ToolLinkCards from '../../components/currency/tool-link-cards/tool-link-cards';

import type {
  MortgageCalculator,
  MortgageRates,
  MortgageValidations,
} from '../../components/mortgage-calculator/shared';

// Configs and Data
import toolLinkConfig from '../../components/mortgage-calculator/shared/tool-link-config';
import PropertyTaxTable from '../../components/property-tax-table/property-tax-table';
import {
  ENTITY_NAME,
  EVENT_TYPE,
  SECTION_NAME,
} from '../../lib/analytics-constants';
import { mortgageCalculatorsRedirect } from '../../lib/utils/redirect';

// Lazy Load Components
const LenderCards = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-lender-cards' */ '../../components/currency/lender-cards/lender-cards'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-related-articles' */ '../../components/currency/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const HlmEntry = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-hlm-entry' */ '@nerdwallet/mortgage-hlm-entry'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const ExpandingLinkList = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-expanding-link-list' */ '../../components/currency/expanding-link-list/expanding-link-list'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

export interface MCStateResultsProps {
  experiments?: any;
  identity?: Record<string, any>;
  location?: Record<string, any>;
  history?: Record<string, any>;
  mortgageCalculator?: MortgageCalculator;
  mortgageRates?: MortgageRates;
  validations?: MortgageValidations;
  impressions?: any;
}

const MCStateResults: React.FC<MCStateResultsProps> = (props) => {
  const {
    location,
    history,
    mortgageCalculator,
    mortgageRates,
    validations,
    impressions,
  } = props;

  const stateListLinks = useMemo(() => {
    const stateList = mortgageCalculator?.stateList ?? [];
    return stateList.map(({ stateName, stateSlug }) => ({
      href: `/mortgages/mortgage-calculator/${stateSlug}`,
      text: `${stateName} mortgage calculator`,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(mortgageCalculator?.stateList)]);

  const data = {
    ...mortgageCalculator,
    validations,
    impressions,
  };

  const stateData = mortgageCalculator.stateData || {};
  const counties = _.get(data, 'counties');

  const { inputs } = data;

  return (
    <AnalyticsProvider>
      <AnalyticsPageView name={ENTITY_NAME.MORTGAGES_STATE_CALCULATOR}>
        <Impression
          eventType={EVENT_TYPE.GEO_PAGE_IMPRESSION}
          entityName={ENTITY_NAME.MORTGAGES_STATE_CALCULATOR}
          payload={{
            additional_attributes: {
              geo_granularity: 'state',
              geo_description: stateData.stateAbbreviation,
            },
          }}
          onClick={() =>
            mortgageCalculatorsRedirect(MORTGAGE_STATE, location, history)
          }
        >
          <Results
            {..._.assign({}, props, {
              className: 'mc-app',
              title: (
                <PageTitle
                  stateName={stateData?.stateName}
                  housingMarketSection={stateData?.housingMarketSection}
                />
              ),
              controlPanel: (
                <MCControlPanel mortgageCalculator={MORTGAGE_STATE} />
              ),
              data,
              lenders: (
                <LenderCards
                  trackingFormName="mc"
                  namedQuery="mortgages_offers_mc"
                  analyticsTrackingConfig={{
                    entityName: ENTITY_NAME.MORTGAGES_STATE_CALCULATOR_OFFER,
                    sectionName: SECTION_NAME.MORTGAGES_STATE_CALCULATOR_OFFER,
                    sectionPosition: 1,
                  }}
                />
              ),
              membershipCta: null,
              resultsPanel: (
                <MCResultsPanel
                  // @ts-ignore - ignore error
                  location={location}
                  history={history}
                  trackingFormName="mc-results"
                  mortgageCalculator={MORTGAGE_STATE}
                />
              ),
              methodology: (
                <Methodology
                  trackingFormName="mc-methodology"
                  impressionTracking
                />
              ),
              relatedArticles: (
                <RelatedArticles
                  entityName={
                    MORTGAGE_STATE_CALCULATOR.RELATED_ARTICLES.entityName
                  }
                  sectionName={
                    MORTGAGE_STATE_CALCULATOR.RELATED_ARTICLES.sectionName
                  }
                  sectionPosition={
                    MORTGAGE_STATE_CALCULATOR.RELATED_ARTICLES.sectionPosition
                  }
                  articles={MORTGAGE_STATE_CALCULATOR.RELATED_ARTICLES.articles}
                />
              ),
              toolLinkCards: (
                <ToolLinkCards
                  header="Other NerdWallet Resources"
                  cards={toolLinkConfig.cards}
                  impressionTrackingName="mortgages_state_calculator_tool_card"
                  data={data}
                  calculatorType="mortgage_state_calculator"
                  analyticsConfig={{
                    sectionName: 'mortgages_state_calculator_tool_card',
                    sectionPosition: 8,
                  }}
                />
              ),
              additionalContent: (
                <ExpandingLinkList
                  heading="Mortgage calculator by state"
                  links={stateListLinks}
                  tracking={{
                    formName: 'seemore_statemc_open',
                    fieldName: 'statemc',
                  }}
                />
              ),
            })}
          >
            <RatesTrend
              rateSummary={_.get(mortgageRates, 'rateSummary')}
              {..._.get(props, 'mortgageCalculator.stateData', {})}
            />
            <HlmEntry zipCode={inputs?.zipCode} />
            <FTHBProgram
              {..._.get(props, 'mortgageCalculator.stateData', {})}
            />
            <RoundupLink {...stateData} />
            <PropertyTaxTable
              counties={counties}
              stateName={stateData.stateName}
            />
            <div />
          </Results>
        </Impression>
      </AnalyticsPageView>
    </AnalyticsProvider>
  );
};

const mapStateToProps = (state) => ({
  mortgageRates: state?.mortgageRates,
  mortgageCalculator: state?.mortgageCalculator,
  identity: state?.identity,
  validations: state?.validations,
  experiments: state?.experiments,
  impressions: state?.impressions,
  zoomed: state?.mortgageCalculator?.zoomed,
});

export default connect(mapStateToProps)(MCStateResults);
